/**
 * Asset Template Library
 * Pre-configured templates for common IT assets
 */

export const ASSET_TEMPLATE_CATEGORIES = [
  { id: 'servers', name: 'Servers', icon: 'server' },
  { id: 'network', name: 'Network', icon: 'wifi' },
  { id: 'endpoints', name: 'Endpoints', icon: 'hard-drive' },
  { id: 'infrastructure', name: 'Infrastructure', icon: 'cloud' },
];

export const ASSET_TEMPLATES = [
  // ============ SERVERS ============
  {
    id: 'windows-server',
    category: 'servers',
    name: 'Windows Server',
    slug: 'windows-server',
    description: 'Windows Server instances with OS details, roles, and management info',
    icon: 'server',
    color: 'blue',
    fieldSchema: [
      { name: 'hostname', label: 'Hostname', type: 'text', required: true },
      { name: 'ip_address', label: 'IP Address', type: 'text', required: true },
      { name: 'os_version', label: 'OS Version', type: 'select', options: ['Windows Server 2022', 'Windows Server 2019', 'Windows Server 2016', 'Windows Server 2012 R2'] },
      { name: 'domain', label: 'Domain', type: 'text' },
      { name: 'roles', label: 'Server Roles', type: 'textarea', hint: 'List installed roles (AD DS, DNS, DHCP, etc.)' },
      { name: 'cpu_cores', label: 'CPU Cores', type: 'number' },
      { name: 'ram_gb', label: 'RAM (GB)', type: 'number' },
      { name: 'storage_gb', label: 'Storage (GB)', type: 'number' },
      { name: 'last_patched', label: 'Last Patched', type: 'date' },
      { name: 'management_url', label: 'Management URL', type: 'url' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'linux-server',
    category: 'servers',
    name: 'Linux Server',
    slug: 'linux-server',
    description: 'Linux server instances with distribution, services, and SSH info',
    icon: 'server',
    color: 'orange',
    fieldSchema: [
      { name: 'hostname', label: 'Hostname', type: 'text', required: true },
      { name: 'ip_address', label: 'IP Address', type: 'text', required: true },
      { name: 'distribution', label: 'Distribution', type: 'select', options: ['Ubuntu', 'CentOS', 'RHEL', 'Debian', 'Rocky Linux', 'AlmaLinux', 'Other'] },
      { name: 'version', label: 'Version', type: 'text' },
      { name: 'services', label: 'Running Services', type: 'textarea', hint: 'List key services (nginx, apache, mysql, etc.)' },
      { name: 'ssh_port', label: 'SSH Port', type: 'number' },
      { name: 'cpu_cores', label: 'CPU Cores', type: 'number' },
      { name: 'ram_gb', label: 'RAM (GB)', type: 'number' },
      { name: 'storage_gb', label: 'Storage (GB)', type: 'number' },
      { name: 'last_patched', label: 'Last Patched', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ============ NETWORK ============
  {
    id: 'network-switch',
    category: 'network',
    name: 'Network Switch',
    slug: 'network-switch',
    description: 'Managed and unmanaged network switches',
    icon: 'wifi',
    color: 'cyan',
    fieldSchema: [
      { name: 'name', label: 'Switch Name', type: 'text', required: true },
      { name: 'ip_address', label: 'Management IP', type: 'text' },
      { name: 'manufacturer', label: 'Manufacturer', type: 'select', options: ['Cisco', 'HP/Aruba', 'Juniper', 'Ubiquiti', 'Netgear', 'Dell', 'Other'] },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'port_count', label: 'Port Count', type: 'number' },
      { name: 'poe_capable', label: 'PoE Capable', type: 'checkbox' },
      { name: 'managed', label: 'Managed Switch', type: 'checkbox' },
      { name: 'firmware_version', label: 'Firmware Version', type: 'text' },
      { name: 'location', label: 'Physical Location', type: 'text' },
      { name: 'management_url', label: 'Management URL', type: 'url' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'router-firewall',
    category: 'network',
    name: 'Router/Firewall',
    slug: 'router-firewall',
    description: 'Routers, firewalls, and UTM appliances',
    icon: 'shield',
    color: 'red',
    fieldSchema: [
      { name: 'name', label: 'Device Name', type: 'text', required: true },
      { name: 'wan_ip', label: 'WAN IP', type: 'text' },
      { name: 'lan_ip', label: 'LAN IP', type: 'text', required: true },
      { name: 'manufacturer', label: 'Manufacturer', type: 'select', options: ['Fortinet', 'Palo Alto', 'SonicWall', 'Cisco', 'pfSense', 'Ubiquiti', 'WatchGuard', 'Other'] },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'firmware_version', label: 'Firmware Version', type: 'text' },
      { name: 'license_expiry', label: 'License Expiry', type: 'date' },
      { name: 'vpn_enabled', label: 'VPN Enabled', type: 'checkbox' },
      { name: 'management_url', label: 'Management URL', type: 'url' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'access-point',
    category: 'network',
    name: 'Access Point',
    slug: 'access-point',
    description: 'Wireless access points and controllers',
    icon: 'wifi',
    color: 'purple',
    fieldSchema: [
      { name: 'name', label: 'AP Name', type: 'text', required: true },
      { name: 'ip_address', label: 'IP Address', type: 'text' },
      { name: 'mac_address', label: 'MAC Address', type: 'text' },
      { name: 'manufacturer', label: 'Manufacturer', type: 'select', options: ['Ubiquiti', 'Cisco Meraki', 'Aruba', 'Ruckus', 'TP-Link', 'Other'] },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'ssids', label: 'SSIDs', type: 'textarea', hint: 'List broadcast SSIDs' },
      { name: 'controller_ip', label: 'Controller IP', type: 'text' },
      { name: 'location', label: 'Physical Location', type: 'text' },
      { name: 'firmware_version', label: 'Firmware Version', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ============ ENDPOINTS ============
  {
    id: 'workstation-desktop',
    category: 'endpoints',
    name: 'Workstation/Desktop',
    slug: 'workstation-desktop',
    description: 'Desktop computers and workstations',
    icon: 'hard-drive',
    color: 'neutral',
    fieldSchema: [
      { name: 'hostname', label: 'Hostname', type: 'text', required: true },
      { name: 'assigned_user', label: 'Assigned User', type: 'text' },
      { name: 'os', label: 'Operating System', type: 'select', options: ['Windows 11', 'Windows 10', 'macOS Sonoma', 'macOS Ventura', 'Linux', 'Other'] },
      { name: 'manufacturer', label: 'Manufacturer', type: 'text' },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'serial_number', label: 'Serial Number', type: 'text' },
      { name: 'cpu', label: 'CPU', type: 'text' },
      { name: 'ram_gb', label: 'RAM (GB)', type: 'number' },
      { name: 'storage_type', label: 'Storage Type', type: 'select', options: ['SSD', 'HDD', 'NVMe'] },
      { name: 'purchase_date', label: 'Purchase Date', type: 'date' },
      { name: 'warranty_expiry', label: 'Warranty Expiry', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'printer',
    category: 'endpoints',
    name: 'Printer',
    slug: 'printer',
    description: 'Network and local printers',
    icon: 'file-text',
    color: 'amber',
    fieldSchema: [
      { name: 'name', label: 'Printer Name', type: 'text', required: true },
      { name: 'ip_address', label: 'IP Address', type: 'text' },
      { name: 'manufacturer', label: 'Manufacturer', type: 'select', options: ['HP', 'Canon', 'Epson', 'Brother', 'Xerox', 'Lexmark', 'Other'] },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'serial_number', label: 'Serial Number', type: 'text' },
      { name: 'printer_type', label: 'Type', type: 'select', options: ['Laser', 'Inkjet', 'Multifunction', 'Label Printer', 'Plotter'] },
      { name: 'color_capable', label: 'Color Capable', type: 'checkbox' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'management_url', label: 'Management URL', type: 'url' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'mobile-device',
    category: 'endpoints',
    name: 'Mobile Device',
    slug: 'mobile-device',
    description: 'Phones, tablets, and mobile devices',
    icon: 'smartphone',
    color: 'green',
    fieldSchema: [
      { name: 'device_name', label: 'Device Name', type: 'text', required: true },
      { name: 'assigned_user', label: 'Assigned User', type: 'text' },
      { name: 'device_type', label: 'Device Type', type: 'select', options: ['iPhone', 'iPad', 'Android Phone', 'Android Tablet', 'Other'] },
      { name: 'manufacturer', label: 'Manufacturer', type: 'text' },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'serial_number', label: 'Serial Number', type: 'text' },
      { name: 'imei', label: 'IMEI', type: 'text' },
      { name: 'phone_number', label: 'Phone Number', type: 'text' },
      { name: 'mdm_enrolled', label: 'MDM Enrolled', type: 'checkbox' },
      { name: 'os_version', label: 'OS Version', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ============ INFRASTRUCTURE ============
  {
    id: 'virtual-machine',
    category: 'infrastructure',
    name: 'Virtual Machine',
    slug: 'virtual-machine',
    description: 'Virtual machines on hypervisors or cloud platforms',
    icon: 'cloud',
    color: 'indigo',
    fieldSchema: [
      { name: 'vm_name', label: 'VM Name', type: 'text', required: true },
      { name: 'hypervisor', label: 'Hypervisor/Platform', type: 'select', options: ['VMware vSphere', 'Hyper-V', 'Proxmox', 'Azure', 'AWS EC2', 'Google Cloud', 'Other'] },
      { name: 'host_server', label: 'Host Server', type: 'text' },
      { name: 'ip_address', label: 'IP Address', type: 'text' },
      { name: 'guest_os', label: 'Guest OS', type: 'text' },
      { name: 'vcpus', label: 'vCPUs', type: 'number' },
      { name: 'ram_gb', label: 'RAM (GB)', type: 'number' },
      { name: 'storage_gb', label: 'Storage (GB)', type: 'number' },
      { name: 'snapshot_enabled', label: 'Snapshots Enabled', type: 'checkbox' },
      { name: 'backup_enabled', label: 'Backup Enabled', type: 'checkbox' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'backup-system',
    category: 'infrastructure',
    name: 'Backup System',
    slug: 'backup-system',
    description: 'Backup solutions, agents, and storage',
    icon: 'hard-drive',
    color: 'teal',
    fieldSchema: [
      { name: 'name', label: 'Backup Name', type: 'text', required: true },
      { name: 'backup_solution', label: 'Backup Solution', type: 'select', options: ['Veeam', 'Datto', 'Acronis', 'Commvault', 'Barracuda', 'Windows Server Backup', 'Other'] },
      { name: 'backup_type', label: 'Backup Type', type: 'select', options: ['Full', 'Incremental', 'Differential', 'CDP'] },
      { name: 'schedule', label: 'Schedule', type: 'text', hint: 'e.g., Daily at 2:00 AM' },
      { name: 'retention_days', label: 'Retention (Days)', type: 'number' },
      { name: 'target_storage', label: 'Target Storage', type: 'text' },
      { name: 'offsite_enabled', label: 'Offsite Replication', type: 'checkbox' },
      { name: 'encryption_enabled', label: 'Encryption Enabled', type: 'checkbox' },
      { name: 'last_successful', label: 'Last Successful Backup', type: 'date' },
      { name: 'management_url', label: 'Management URL', type: 'url' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
];
